import React, { useState, useEffect } from 'react';
import { CloseIcon, PhoneIcon, EmailIcon, WhatsAppIcon, LocationIcon } from '../Icons/Icons';
import LazyImage from '../LazyImage/LazyImage';
import './ContactAgentModal.css';

function ContactAgentModal({ agent, isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    contactMethod: 'email'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^(\+92|0)?[0-9]{10}$/.test(formData.phone.replace(/[\s-]/g, ''))) {
      newErrors.phone = 'Please enter a valid Pakistani phone number';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      console.log('Contact Agent Form Data:', {
        agent: agent.name,
        agentId: agent.id,
        ...formData
      });
      setIsSubmitting(false);
      setSubmitSuccess(true);

      // Reset form after 2 seconds and close modal
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          contactMethod: 'email'
        });
        setSubmitSuccess(false);
        onClose();
      }, 2000);
    }, 1500);
  };

  const handleQuickContact = (method) => {
    if (method === 'call') {
      window.location.href = `tel:${agent.phone}`;
    } else if (method === 'whatsapp') {
      const message = encodeURIComponent(`Hi ${agent.name}, I'm interested in your real estate services.`);
      window.open(`https://wa.me/${agent.phone.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
    } else if (method === 'email') {
      window.location.href = `mailto:${agent.email}?subject=Property Inquiry`;
    }
  };

  if (!isOpen || !agent) return null;

  return (
    <div className="contact-modal-overlay" onClick={onClose}>
      <div className="contact-modal-container" onClick={(e) => e.stopPropagation()}>
        <button className="contact-modal-close" onClick={onClose} aria-label="Close modal">
          <CloseIcon size={24} color="#2d3436" />
        </button>

        <div className="contact-modal-content">
          {/* Agent Info Section */}
          <div className="contact-modal-agent-info">
            <div className="contact-modal-agent-header">
              <div className="contact-modal-agent-avatar">
                <LazyImage
                  src={agent.image}
                  alt={agent.name}
                  aspectRatio="1/1"
                />
                {agent.verified && (
                  <div className="contact-modal-verified-badge">✓</div>
                )}
              </div>
              <div className="contact-modal-agent-details">
                <h2 className="contact-modal-agent-name">{agent.name}</h2>
                <p className="contact-modal-agent-designation">{agent.designation}</p>
                <div className="contact-modal-agent-rating">
                  <span className="modal-rating-star">★</span>
                  <span className="modal-rating-value">{agent.rating}</span>
                  <span className="modal-rating-reviews">({agent.reviews} reviews)</span>
                </div>
                <div className="contact-modal-agent-location">
                  <LocationIcon size={14} color="#636e72" />
                  <span>{agent.city}</span>
                </div>
              </div>
            </div>

            <div className="contact-modal-agent-stats">
              <div className="modal-stat-item">
                <span className="modal-stat-value">{agent.properties}</span>
                <span className="modal-stat-label">Properties</span>
              </div>
              <div className="modal-stat-item">
                <span className="modal-stat-value">{agent.experience}</span>
                <span className="modal-stat-label">Experience</span>
              </div>
            </div>

            {/* Quick Contact Buttons */}
            <div className="contact-modal-quick-actions">
              <button
                className="quick-action-btn quick-action-call"
                onClick={() => handleQuickContact('call')}
              >
                <PhoneIcon size={18} color="#ffffff" />
                <span>Call</span>
              </button>
              <button
                className="quick-action-btn quick-action-whatsapp"
                onClick={() => handleQuickContact('whatsapp')}
              >
                <WhatsAppIcon size={18} color="#ffffff" />
                <span>WhatsApp</span>
              </button>
              <button
                className="quick-action-btn quick-action-email"
                onClick={() => handleQuickContact('email')}
              >
                <EmailIcon size={18} color="#ffffff" />
                <span>Email</span>
              </button>
            </div>
          </div>

          {/* Contact Form Section */}
          <div className="contact-modal-form-section">
            <h3 className="contact-form-title">Send a Message</h3>
            <p className="contact-form-subtitle">
              Fill out the form below and {agent.name} will get back to you shortly
            </p>

            {submitSuccess ? (
              <div className="contact-success-message">
                <div className="success-icon">✓</div>
                <h4>Message Sent Successfully!</h4>
                <p>{agent.name} will contact you soon</p>
              </div>
            ) : (
              <form className="contact-agent-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    Your Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`form-input ${errors.name ? 'form-input-error' : ''}`}
                    placeholder="Enter your full name"
                  />
                  {errors.name && <span className="form-error">{errors.name}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">
                      Email Address <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`form-input ${errors.email ? 'form-input-error' : ''}`}
                      placeholder="your.email@example.com"
                    />
                    {errors.email && <span className="form-error">{errors.email}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">
                      Phone Number <span className="required">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`form-input ${errors.phone ? 'form-input-error' : ''}`}
                      placeholder="+92 300 1234567"
                    />
                    {errors.phone && <span className="form-error">{errors.phone}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="contactMethod" className="form-label">
                    Preferred Contact Method
                  </label>
                  <select
                    id="contactMethod"
                    name="contactMethod"
                    value={formData.contactMethod}
                    onChange={handleChange}
                    className="form-select"
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone Call</option>
                    <option value="whatsapp">WhatsApp</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    Your Message <span className="required">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className={`form-textarea ${errors.message ? 'form-input-error' : ''}`}
                    placeholder="Tell us about your property requirements..."
                    rows="5"
                  ></textarea>
                  {errors.message && <span className="form-error">{errors.message}</span>}
                </div>

                <button
                  type="submit"
                  className="btn-submit-contact"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactAgentModal;
